import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { makeAutoObservable } from "mobx";

import { ApiConnector } from "../../api/api-connector";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: string[];
}

function prettifyToolName(raw: string): string {
  const match = raw.match(/api_v\d+_(.+?)_(get|post|put|patch|delete)$/);
  if (match) {
    return match[1].replace(/_/g, "/");
  }
  return raw.replace(/_/g, " ");
}

export class ChatModel {
  messages: ChatMessage[] = [];
  input = "";
  loading = false;
  conversationId: string | null = null;
  activeTools: string[] = [];

  private abortController: AbortController | undefined;

  constructor(private api: ApiConnector) {
    makeAutoObservable(this);
  }

  async sendMessage() {
    const trimmed = this.input.trim();
    if (!trimmed || this.loading) return;

    this.messages.push({ role: "user", content: trimmed });
    this.messages.push({ role: "assistant", content: "", toolCalls: [] });
    this.input = "";
    this.loading = true;
    this.activeTools = [];

    this.abortController?.abort();
    const abort = new AbortController();
    this.abortController = abort;

    try {
      const response = await this.api.streamingFetch(
        "/agent/chat/stream",
        { message: trimmed, conversation_id: this.conversationId },
        abort.signal,
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("No response body");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // skip malformed JSON
          }
        }
      }
    } catch (err) {
      if (abort.signal.aborted) return;
      const last = this.messages[this.messages.length - 1];
      if (last?.role === "assistant" && last.content === "") {
        last.content = i18n._(
          msg`Sorry, something went wrong. Please try again.`,
        );
      }
      console.error("Chat error:", err);
    } finally {
      this.loading = false;
      this.activeTools = [];
    }
  }

  private handleEvent(event: { type: string; [key: string]: unknown }) {
    const last = this.messages[this.messages.length - 1];
    if (!last || last.role !== "assistant") return;

    switch (event.type) {
      case "text_delta":
        last.content += event.content as string;
        break;
      case "tool_call": {
        const toolName = prettifyToolName(event.tool_name as string);
        this.activeTools = [...this.activeTools, toolName];
        last.toolCalls = [...(last.toolCalls ?? []), toolName];
        break;
      }
      case "tool_result": {
        const toolName = prettifyToolName(event.tool_name as string);
        this.activeTools = this.activeTools.filter((t) => t !== toolName);
        break;
      }
      case "done":
        this.conversationId = event.conversation_id as string;
        this.activeTools = [];
        break;
    }
  }

  newConversation() {
    this.abortController?.abort();
    this.messages = [];
    this.conversationId = null;
    this.input = "";
    this.activeTools = [];
  }

  handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void this.sendMessage();
    }
  }

  dispose() {
    this.abortController?.abort();
  }
}

export function createChatModel(api: ApiConnector) {
  return new ChatModel(api);
}
